import { useState } from 'react';
import { Link } from 'wouter';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/error-state';
import { StoreTaskHistory } from '@/components/retailer/StoreTaskHistory';
import { CampaignsIssuesView } from '@/features/store-profiles/components/retailer/CampaignsIssuesView';
import { RetailerStoreMap } from '@/features/store-profiles/components/retailer/RetailerStoreMap';
import { useStoreProfileDetail } from '@/features/store-profiles/hooks/useStoreProfilesData';
import type { SpatialEntity } from '@/features/store-profiles/lib/spatialEntity';
import type { StoreProfileDetail } from '@/features/store-profiles/types';

export type StoreRecordSection = 'information' | 'media' | 'floorplan' | 'campaigns' | 'history';

export type StoreProfileSection = Exclude<StoreRecordSection, 'information' | 'media'>;

const TABS: Array<{ id: StoreRecordSection; label: string }> = [
  { id: 'information', label: 'Store Information' },
  { id: 'media', label: 'Media Formats' },
  { id: 'floorplan', label: 'Floorplan' },
  { id: 'campaigns', label: 'Campaigns' },
  { id: 'history', label: 'History' },
];

interface StoreRecordTabsProps {
  active: StoreRecordSection;
  onChange: (section: StoreRecordSection) => void;
}

export function StoreRecordTabs({ active, onChange }: StoreRecordTabsProps) {
  return (
    <nav aria-label="Store sections" className="flex flex-wrap gap-2">
      {TABS.map(({ id, label }) => {
        const isActive = active === id;
        return (
          <Button
            key={id}
            type="button"
            variant={isActive ? 'default' : 'outline'}
            size="sm"
            onClick={() => onChange(id)}
            className="h-9"
            aria-current={isActive ? 'page' : undefined}
          >
            {label}
          </Button>
        );
      })}
    </nav>
  );
}

interface StoreRecordTwinPanelProps {
  storeId: number;
  section: StoreProfileSection;
  onSectionChange: (section: StoreRecordSection) => void;
}

export function StoreRecordTwinPanel({
  storeId,
  section,
  onSectionChange,
}: StoreRecordTwinPanelProps) {
  const storeIdParam = String(storeId);
  const { profile, isLoading, isError, error } = useStoreProfileDetail(storeIdParam);
  const [selection, setSelection] = useState<{ kind: SpatialEntity['kind']; id: string } | null>(null);

  if (section === 'history') {
    return <StoreTaskHistory storeId={storeId} />;
  }

  const focusOnFloorplan = (next: { kind: SpatialEntity['kind']; id: string }) => {
    setSelection(next);
    onSectionChange('floorplan');
  };

  const focusCampaign = (campaign: StoreProfileDetail['campaigns'][number]) => {
    if (campaign.floorplanZoneId) {
      focusOnFloorplan({ kind: 'zone', id: campaign.floorplanZoneId });
      return;
    }
    if (campaign.aisleId) {
      focusOnFloorplan({ kind: 'aisle', id: campaign.aisleId });
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[240px] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" aria-label="Loading store profile" />
      </div>
    );
  }

  if (isError || !profile) {
    return (
      <ErrorState
        title="Could not load this store profile"
        message={error instanceof Error ? error.message : 'The floorplan, campaigns, and history are unavailable.'}
        showRetry={false}
      />
    );
  }

  const floorplanMissing = profile.storeAreasCount === 0 && !profile.floorplanImageUrl;

  return (
    <div className="space-y-3">
      {section === 'floorplan' && floorplanMissing ? (
        <p className="text-sm text-muted-foreground">
          No floorplan yet.{' '}
          <Link href={`/super-admin/store-profiles/${storeIdParam}`} className="font-medium text-blue-700 underline">
            Set it up in Store Profiles
          </Link>
        </p>
      ) : null}
      <TwinSection
        section={section}
        profile={profile}
        selection={selection}
        onSelectionChange={setSelection}
        onFocusLocation={focusOnFloorplan}
        onFocusCampaign={focusCampaign}
      />
    </div>
  );
}

function TwinSection({
  section,
  profile,
  selection,
  onSelectionChange,
  onFocusLocation,
  onFocusCampaign,
}: {
  section: StoreProfileSection;
  profile: StoreProfileDetail;
  selection: { kind: SpatialEntity['kind']; id: string } | null;
  onSelectionChange: (selection: { kind: SpatialEntity['kind']; id: string } | null) => void;
  onFocusLocation: (selection: { kind: SpatialEntity['kind']; id: string }) => void;
  onFocusCampaign: (campaign: StoreProfileDetail['campaigns'][number]) => void;
}) {
  switch (section) {
    case 'floorplan':
      return (
        <RetailerStoreMap
          profile={profile}
          asOf={null}
          selection={selection}
          onSelectionChange={onSelectionChange}
        />
      );
    case 'campaigns':
      return (
        <CampaignsIssuesView
          profile={profile}
          asOf={null}
          onFocusLocation={onFocusLocation}
          onFocusCampaign={onFocusCampaign}
        />
      );
    case 'history':
      return <StoreTaskHistory storeId={Number(profile.id)} />;
    default: {
      const exhaustive: never = section;
      return exhaustive;
    }
  }
}
